import apiFetch from "@wordpress/api-fetch";
import { useDispatch, useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Button from "../../button";
import { transition } from "../auth-state-machine";
import { authStore } from "../auth-store";
import { handleApiErrorOrReload } from "../auth-utils";
import { defaultSubmitTextForStage } from "../default-copy";

type Props = { copySubmitOverride?: string };

export const JoinExistingStage: React.FC<Props> = ({ copySubmitOverride }) => {
	const intent = useSelect((select) => select(authStore).getIntent(), []);
	const error = useSelect((select) => select(authStore).getError(), []);
	const isSubmitting = useSelect(
		(select) => select(authStore).isSubmitting(),
		[],
	);
	const prefillFirst = useSelect(
		(select) => select(authStore).getPrefillFirstName(),
		[],
	);
	const prefillLast = useSelect(
		(select) => select(authStore).getPrefillLastName(),
		[],
	);
	const { setError, setSubmitting, setStage } = useDispatch(authStore);
	const [firstName, setFirstName] = useState(prefillFirst);
	const [lastName, setLastName] = useState(prefillLast);
	const abortRef = useRef<AbortController | null>(null);

	useEffect(() => () => abortRef.current?.abort(), []);

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		abortRef.current?.abort();
		const ac = new AbortController();
		abortRef.current = ac;
		try {
			await apiFetch<{ success: true }>({
				path: "/leat/v1/join-program",
				method: "POST",
				data: {
					...(firstName && { firstName }),
					...(lastName && { lastName }),
				},
				signal: ac.signal,
			});
			const next = transition(
				{ stage: "joinExisting", email: "" },
				{ type: "joinSuccess" },
				intent,
			);
			setStage(next.stage);
		} catch (err) {
			if ((err as { name?: string })?.name === "AbortError") return;
			const msg = handleApiErrorOrReload(err);
			if (msg) setError(msg);
		} finally {
			if (!ac.signal.aborted) setSubmitting(false);
		}
	};

	return (
		<form onSubmit={onSubmit} noValidate>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-fname">{__("First name", "leat-crm")}</label>
				<input
					id="leat-auth-fname"
					type="text"
					autoComplete="given-name"
					value={firstName}
					onChange={(e) => setFirstName(e.target.value)}
				/>
			</div>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-lname">{__("Last name", "leat-crm")}</label>
				<input
					id="leat-auth-lname"
					type="text"
					autoComplete="family-name"
					value={lastName}
					onChange={(e) => setLastName(e.target.value)}
				/>
			</div>
			{error && (
				<div className="leat-auth-modal__submit-error" role="alert">
					{error}
				</div>
			)}
			<Button
				type="submit"
				variant="contained"
				className="leat-auth-modal__submit leat-button--pill"
				disabled={isSubmitting}
			>
				{isSubmitting
					? __("Joining…", "leat-crm")
					: (copySubmitOverride ?? defaultSubmitTextForStage("joinExisting"))}
			</Button>
		</form>
	);
};
